import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  PLATFORM_ID,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import type { Lyric } from '@lyricstream/shared';
import { LANG_SIGNAL, LoggerService, LyricCardComponent, LyricService } from '@lyricstream/shared';
import { TranslateService } from '@ngx-translate/core';
import { isNew, relativeTimeLabel, timeAgo } from '../../shared/utils/lyric-format.utils';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'ls-lyric-list-section',
  imports: [TranslateModule, LyricCardComponent],
  templateUrl: './lyric-list-section.component.html',
  styleUrl: './lyric-list-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LyricListSectionComponent {
  // ── Services ──────────────────────────────────────────────────────────────
  private readonly lyricService = inject(LyricService);
  private readonly logger = inject(LoggerService);
  private readonly platformId = inject(PLATFORM_ID);
  protected readonly lang = inject(LANG_SIGNAL);
  private readonly translate = inject(TranslateService);
  private readonly t = (key: string, params?: Record<string, unknown>): string => this.translate.instant(key, params);

  // ── Inputs ────────────────────────────────────────────────────────────────
  readonly mode = input.required<'popular' | 'latest'>();
  readonly paginate = input<boolean>(false);
  readonly pageSize = input<number>(10);

  // ── State signals ─────────────────────────────────────────────────────────
  private readonly _items = signal<Lyric[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _page = signal(1);
  private readonly _totalPages = signal(1);

  // ── Readonly exposure ─────────────────────────────────────────────────────
  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly page = this._page.asReadonly();
  readonly totalPages = this._totalPages.asReadonly();

  // ── Computed signals ──────────────────────────────────────────────────────
  readonly hasNext = computed(() => this._page() < this._totalPages());
  readonly hasPrev = computed(() => this._page() > 1);

  // ── Helpers (exposés au template) ─────────────────────────────────────────
  protected readonly isNew = isNew;

  constructor() {
    afterNextRender(() => {
      if (isPlatformBrowser(this.platformId)) {
        void this.load(1);
      }
    });
  }

  /** Même route que l'ancien [routerLink]="['/', lang(), 'decouvrir', item.slug]" — ls-lyric-card
   *  attend une string pour son input `href`, pas un tableau RouterLink. */
  protected cardHref(item: Lyric): string {
    return `/${this.lang()}/decouvrir/${item.slug}`;
  }

  /** Mode "latest" uniquement : temps relatif d'ajout — dépend de quelle date la page veut
   *  afficher (createdAt ici, mais pourrait être updatedAt ailleurs), donc reste construit ici
   *  plutôt que dérivé génériquement par ls-lyric-card. Mode "popular" : vues, cf. [showViewCount]
   *  sur le template — dérivé en interne par ls-lyric-card depuis lyric() directement. */
  protected cardMetric(item: Lyric): { kind: 'relativeTime'; value: string } | undefined {
    return this.mode() === 'latest'
      ? { kind: 'relativeTime', value: relativeTimeLabel(item.createdAtUnit, item.createdAtCount, item.createdAtTime, this.t) || item.createdAtDisplay || timeAgo(item.createdAt, this.t) }
      : undefined;
  }

  protected async load(page = 1): Promise<void> {
    this._loading.set(true);
    this._error.set(null);
    try {
      const result =
        this.mode() === 'popular'
          ? await this.lyricService.getPopularLyrics(page, this.pageSize())
          : await this.lyricService.getLatestLyrics(page, this.pageSize());
      //this.logger.debug(`Loaded ${result.items.length} lyrics for page ${page} (${this.mode()})`, result);
      this._items.set(result.items);
      this._page.set(result.page);
      this._totalPages.set(result.totalPages);
    } catch (err) {
      this._error.set(err instanceof Error ? err.message : this.t('TIME_SHORT.LOAD_ERROR'));
    } finally {
      this._loading.set(false);
    }
  }

  protected retry(): void {
    void this.load(this._page());
  }

  protected nextPage(): void {
    if (this.hasNext()) void this.load(this._page() + 1);
  }

  protected prevPage(): void {
    if (this.hasPrev()) void this.load(this._page() - 1);
  }
}
